import { useState, useEffect, useRef, useCallback } from 'react';
import { Icon } from './Icon.tsx';
import { navigate } from '../lib/router.ts';
import type { DataPort } from '../adapters/port.ts';
import type { PlanPendiente } from '../lib/contracts.ts';

/**
 * Campanita de notificaciones. En el margen superior derecho, muestra de UN
 * VISTAZO si hay algo que espera TU respuesta.
 *
 * 🔑 Qué notifica: los planes esperando tu aprobación (lo mismo que la sección
 * Aprobaciones). Son las «modificaciones que necesitan respuesta del usuario».
 *
 * Colorada con el número cuando hay pendientes; sin color cuando no hay nada.
 * La regla es la misma que en el Dashboard: rojo = requiere tu acción.
 */
export function NotificationBell({ port }: { port: DataPort }) {
  const [planes, setPlanes] = useState<readonly PlanPendiente[]>([]);
  const [abierto, setAbierto] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const cargar = useCallback(async () => {
    const r = await port.pendingPlans();
    setPlanes(r.kind === 'ok' ? r.value : []);
  }, [port]);

  // Al montar, y cada vez que la ventana vuelve al foco: si aprobaste algo en
  // otra pestaña, la campana se pone al día sin recargar toda la página.
  useEffect(() => {
    void cargar();
    const onFocus = () => void cargar();
    window.addEventListener('focus', onFocus);
    // También al cambiar de vista dentro de la app (por si aprobaste recién).
    window.addEventListener('hashchange', onFocus);
    return () => { window.removeEventListener('focus', onFocus); window.removeEventListener('hashchange', onFocus); };
  }, [cargar]);

  // Cerrar al clickear afuera.
  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setAbierto(false); };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, [abierto]);

  const n = planes.length;
  const hay = n > 0;

  return (
    <div className="bell" ref={ref}>
      <button
        type="button"
        className={'bell__btn' + (hay ? ' bell__btn--alert' : '')}
        aria-label={hay ? `${n} ${n === 1 ? 'notificación pendiente' : 'notificaciones pendientes'}` : 'Sin notificaciones'}
        aria-expanded={abierto}
        onClick={() => { setAbierto((v) => !v); if (!abierto) void cargar(); }}
      >
        <Icon name="bell" size={19} />
        {hay && <span className="bell__badge">{n}</span>}
      </button>

      {abierto && (
        <div className="bell__menu" role="menu">
          <div className="bell__head">
            <span>{hay ? `${n} ${n === 1 ? 'cosa espera' : 'cosas esperan'} tu respuesta` : 'Sin pendientes'}</span>
          </div>

          {!hay && (
            <p className="bell__empty">Nada requiere tu decisión ahora mismo. Cuando entre un plan para aprobar, aparece acá.</p>
          )}

          {planes.map((p) => (
            <button
              key={p.proyecto}
              type="button"
              className="bell__item"
              role="menuitem"
              onClick={() => { setAbierto(false); navigate('/approvals'); }}
            >
              <span className="bell__item-title">{p.entregable}</span>
              <span className="bell__item-sub">
                {p.proyecto} · {p.minutos} min · espera tu aprobación
              </span>
            </button>
          ))}

          {hay && (
            <button type="button" className="bell__all" onClick={() => { setAbierto(false); navigate('/approvals'); }}>
              Ir a Aprobaciones →
            </button>
          )}
        </div>
      )}
    </div>
  );
}
